import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import accessLocalStorage from '../../services/accessLocalStorage';
import { extractExtension, displayRamal, displayAgentName, isUsefulAgent, displayQueue, extensionsInActiveAssignments } from './utils';
import './styles.css';

const CACHE_KEY = 'callcenterV2_state';
const CACHE_VERSION = 1;
const CACHE_TTL = 30 * 60 * 1000;
const METRICS_REFRESH_INTERVAL = 30000;
const BACKOFF_BASE = 1000;
const BACKOFF_MAX = 30000;

const VISIBILITY_KEY = 'callcenterV2_visibility';
const DEFAULT_VISIBILITY = {
  header: true,
  kpiPrimary: true,
  kpiSecondary: true,
  queuesPanel: true,
  callsPanel: true,
  agentsPanel: true,
};

const loadVisibility = () => {
  try {
    const saved = localStorage.getItem(VISIBILITY_KEY);
    if (saved) {
      return { ...DEFAULT_VISIBILITY, ...JSON.parse(saved) };
    }
  } catch (e) {}
  return DEFAULT_VISIBILITY;
};

const saveVisibility = (visibility) => {
  try {
    localStorage.setItem(VISIBILITY_KEY, JSON.stringify(visibility));
  } catch (e) {}
};

const getWsBase = async () => {
  const envBase = process.env.REACT_APP_CALLCENTER_V2_BASE_URL;
  if (envBase) return envBase;
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getApiBase = async () => {
  const wsBase = await getWsBase();
  return wsBase;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

const saveToCache = (state) => {
  try {
    const cacheData = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      state: {
        agents: state.agents,
        queues: state.queues,
        calls: state.calls,
        assignments: state.assignments,
        lastSeq: state.lastSeq,
      },
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {}
};

const loadFromCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const data = JSON.parse(cached);
    if (data.version !== CACHE_VERSION) return null;
    if (Date.now() - data.timestamp > CACHE_TTL) return null;
    return data.state;
  } catch (e) {
    return null;
  }
};

const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (e) {}
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

// Render today's queue counter from analytics, fallback to '—' when the analytics
// pipeline has no data for that queue. Never falls back to AMI's QueueParams
// lifetime counters in `state.queues` — those are cumulative since last queue
// reload and showing them as "hoje" misleads supervisors.
const formatQueueCount = (analytics, field) => {
  if (!analytics) return '—';
  const v = analytics[field];
  if (v == null) return '—';
  return Number(v).toLocaleString('pt-BR');
};

// Render today's queue duration (asa/aht) from analytics, fallback to '—'.
// Same rationale as formatQueueCount.
const formatQueueDuration = (analytics, field) => {
  if (!analytics) return '—';
  const v = analytics[field];
  if (v == null) return '—';
  return formatDuration(Math.round(Number(v)));
};

const formatTime = (isoString) => {
  if (!isoString) return '--';
  const date = new Date(isoString);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const getElapsedSeconds = (timeValue) => {
  if (!timeValue) return 0;
  const start = new Date(timeValue).getTime();
  if (isNaN(start)) return 0;
  const now = Date.now();
  return Math.max(0, Math.floor((now - start) / 1000));
};

const getStatusColor = (status) => {
  const colors = {
    online: '#22c55e',
    available: '#22c55e',
    ringing: '#f59e0b',
    busy: '#ef4444',
    inuse: '#ef4444',
    paused: '#8b5cf6',
    offline: '#6b7280',
    unavailable: '#6b7280',
  };
  return colors[status?.toLowerCase()] || colors.offline;
};

// AMI Device State codes:
// 0=UNKNOWN, 1=NOT_INUSE, 2=INUSE, 3=BUSY, 4=INVALID, 5=UNAVAILABLE, 6=RINGING, 7=RINGINUSE, 8=ONHOLD
//
// effectiveInCall takes priority over device state because Local/<ext>@from-queue
// QueueMembers (used to ring SIP + WebRTC simultaneously) never carry device
// state — only the connected assignment is authoritative.
const getAgentStatusLabel = (agent) => {
  if (agent.paused) return 'Pausado';
  if (agent.effectiveInCall) return 'Em chamada';

  switch (agent.status) {
    case 1: return 'Disponível';      // NOT_INUSE
    case 0: return 'Disponível';      // UNKNOWN (fallback)
    case 2: return 'Em chamada';      // INUSE
    case 3: return 'Ocupado';         // BUSY
    case 6: return 'Tocando';         // RINGING
    case 7: return 'Tocando';         // RINGINUSE
    case 8: return 'Em espera';       // ONHOLD
    case 5: return 'Indisponível';    // UNAVAILABLE
    case 4: return 'Offline';         // INVALID
    default: return 'Offline';
  }
};

const getAgentStatusClass = (agent) => {
  if (agent.paused) return 'paused';
  if (agent.effectiveInCall) return 'busy';

  switch (agent.status) {
    case 1:                           // NOT_INUSE
    case 0: return 'available';       // UNKNOWN (fallback)
    case 2:                           // INUSE
    case 3:                           // BUSY
    case 6:                           // RINGING
    case 7:                           // RINGINUSE
    case 8: return 'busy';            // ONHOLD
    case 5: return 'unavailable';     // UNAVAILABLE
    case 4:                           // INVALID
    default: return 'offline';
  }
};

function CallcenterV2() {
  const cachedState = loadFromCache();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState(cachedState || {
    agents: {},
    queues: {},
    calls: {},
    assignments: {},
    lastSeq: 0,
    tenantPrefix: null,
  });
  const [metrics, setMetrics] = useState({
    asa: null,
    aht: null,
    sla: null,
    abandonRate: null,
    occupancy: null,
    servicedToday: 0,
    abandonedToday: 0,
    avgWaitTime: 0,
  });
  // Per-queue analytics (today's data). Keyed by queue name (== queue_key).
  // Absent / null fields render as '—' so we never show AMI's lifetime
  // QueueParams counters as if they were "hoje" stats.
  const [queueMetrics, setQueueMetrics] = useState({});
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [drilldownQueue, setDrilldownQueue] = useState(null);
  const [visibility, setVisibility] = useState(loadVisibility);

  useEffect(() => {
    const handleVisibilityChange = (e) => {
      setVisibility(e.detail);
    };
    window.addEventListener('ccv2-visibility-change', handleVisibilityChange);
    return () => window.removeEventListener('ccv2-visibility-change', handleVisibilityChange);
  }, []);

  const socketRef = useRef(null);
  const tenantIdRef = useRef(null);
  const mountedRef = useRef(true);
  const metricsIntervalRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      if (document.visibilityState !== 'visible') return;
      const apiBase = await getApiBase();
      const tenantId = tenantIdRef.current;
      if (!tenantId) return;

      const [kpiRes, queuesRes] = await Promise.all([
        fetch(`${apiBase}/v2/analytics/metrics/${tenantId}/kpis`),
        fetch(`${apiBase}/v2/analytics/metrics/${tenantId}/queues`),
      ]);

      if (kpiRes.ok) {
        const data = await kpiRes.json();
        if (mountedRef.current) {
          setMetrics({
            asa: data.asa ?? null,
            aht: data.aht ?? null,
            sla: data.sla ?? null,
            abandonRate: data.abandon_rate ?? data.abandonRate ?? null,
            occupancy: data.occupancy ?? null,
            servicedToday: data.serviced_today || data.servicedToday || 0,
            abandonedToday: data.abandoned_today || data.abandonedToday || 0,
            avgWaitTime: data.avg_wait_time || data.avgWaitTime || 0,
          });
        }
      }

      if (queuesRes.ok) {
        const data = await queuesRes.json();
        if (mountedRef.current) {
          const map = {};
          for (const q of data.queues || []) {
            const k = q.queue_key || q.queueKey || q.name;
            if (k) map[k] = q;
          }
          setQueueMetrics(map);
        }
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (connected && tenantIdRef.current) {
      fetchMetrics();
      metricsIntervalRef.current = setInterval(fetchMetrics, METRICS_REFRESH_INTERVAL);
    }
    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
        metricsIntervalRef.current = null;
      }
    };
  }, [connected, fetchMetrics]);

  useEffect(() => {
    if (state.lastSeq > 0) {
      saveToCache(state);
    }
  }, [state]);

  const applyEvent = useCallback((event) => {
    setState((prev) => {
      // cdr.completed is post-processing data emitted on Hangup; it must NOT
      // mutate UI state.calls. Backend gateway already filters via whitelist,
      // but this is defense-in-depth in case an old build broadcasts it.
      if (event?.type === 'cdr.completed') return prev;

      // agent.updated is emitted by the CRUD service when displayName/extension/
      // email change in /callcenterv2/agents. Realtime state is keyed by AMI
      // interface (PJSIP/4800, Local/4800@.../n) so a single agentKey may map
      // to several entries; patch displayName into all of them so the
      // "Atendentes" panel reflects the rename without a page reload.
      if (event?.type === 'agent.updated') {
        const { agentKey, displayName } = event.payload || {};
        if (!agentKey) return prev;
        const target = String(agentKey);
        const newAgents = {};
        let changed = false;
        for (const [k, a] of Object.entries(prev.agents)) {
          const ext = a.extension || extractExtension(a.interface || '');
          const matches = String(ext) === target || a.interface === agentKey || a.memberId === agentKey;
          if (matches && displayName && a.displayName !== displayName) {
            newAgents[k] = { ...a, displayName };
            changed = true;
          } else {
            newAgents[k] = a;
          }
        }
        if (!changed) return { ...prev, lastSeq: Math.max(prev.lastSeq, event.seq) };
        return { ...prev, agents: newAgents, lastSeq: Math.max(prev.lastSeq, event.seq) };
      }

      const entityMap = { agent: 'agents', queue: 'queues', call: 'calls', assignment: 'assignments' };
      const stateKey = entityMap[event.entityType];
      if (!stateKey) return prev;

      const newState = { ...prev };

      const isDelete = event.type.endsWith('.delete') ||
                       event.type.endsWith('.ended') ||
                       event.type.endsWith('.left') ||
                       event.type.endsWith('.abandoned');
      const isUpsert = event.type.endsWith('.upsert') ||
                       event.type.endsWith('.queued') ||
                       event.type.endsWith('.ringing') ||
                       event.type.endsWith('.answered');

      // Reject agent upserts without usable identity (initial AMI QueueMember
      // enumeration sometimes emits payloads with interface=null/undefined).
      if (isUpsert && event.entityType === 'agent' && !isUsefulAgent(event.payload)) {
        return prev;
      }

      if (isUpsert) {
        const prevEntity = prev[stateKey]?.[event.entityId] || {};
        const merged = { ...prevEntity, ...event.payload };
        // Sticky enteredAt for calls: preserve original join time across upserts
        // (position updates, AgentCalled, etc.) so the TEMPO ticker keeps growing.
        if (event.entityType === 'call' && prevEntity.enteredAt && !event.payload.enteredAt) {
          merged.enteredAt = prevEntity.enteredAt;
        }
        newState[stateKey] = {
          ...prev[stateKey],
          [event.entityId]: merged,
        };
      } else if (isDelete) {
        const updated = { ...prev[stateKey] };
        delete updated[event.entityId];
        newState[stateKey] = updated;
      }

      newState.lastSeq = Math.max(prev.lastSeq, event.seq);
      return newState;
    });
  }, []);

  const applySnapshot = useCallback((snapshot) => {
    const toMap = (arr) => {
      const map = {};
      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          const id = item.memberId || item.callId || item.queueId || item.assignmentId || item.uniqueId || item.id;
          if (id) map[id] = item;
        });
      } else if (typeof arr === 'object' && arr !== null) {
        return arr;
      }
      return map;
    };

    setState({
      agents: toMap(snapshot.agents),
      queues: toMap(snapshot.queues),
      calls: toMap(snapshot.calls),
      assignments: toMap(snapshot.assignments),
      lastSeq: snapshot.seq || snapshot.lastSeq || 0,
      tenantPrefix: snapshot.tenant?.namePrefix ?? null,
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let socket = null;

    const connect = async () => {
      try {
        setError(null);
        const wsBase = await getWsBase();
        const tenantId = await getTenantId();
        const token = await getToken();

        tenantIdRef.current = tenantId;

        const reconnectDelay = Math.min(BACKOFF_BASE * Math.pow(2, reconnectAttempt), BACKOFF_MAX);

        socket = io(`${wsBase}/v2/callcenter`, {
          path: '/ccv2-socket/',
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionDelay: reconnectDelay,
          reconnectionDelayMax: BACKOFF_MAX,
          reconnectionAttempts: Infinity,
        });

        socketRef.current = socket;

        socket.on('connect', () => {
          if (!mountedRef.current) return;
          setConnected(true);
          setReconnectAttempt(0);
          setError(null);

          const subscribeTimeout = setTimeout(() => {
            if (mountedRef.current) {
              setLoading(false);
              setError('Timeout ao conectar ao servidor. Tentando reconectar...');
            }
          }, 10000);

          socket.emit('subscribe', {
            token,
            tenantId,
            afterSeq: state.lastSeq,
          }, (response) => {
            clearTimeout(subscribeTimeout);
            if (!mountedRef.current) return;

            if (response && response.success && response.snapshot) {
              applySnapshot(response.snapshot);
            } else if (response && response.needsFullSync) {
              clearCache();
              applySnapshot(response.snapshot || { agents: [], queues: [], calls: [], assignments: [], seq: 0 });
            } else if (response && !response.success) {
              setError(response.error || 'Falha na autenticação');
            }
            setLoading(false);
          });
        });

        socket.on('disconnect', (reason) => {
          if (!mountedRef.current) return;
          setConnected(false);
          if (reason === 'io server disconnect') {
            setReconnectAttempt((prev) => prev + 1);
          }
        });

        socket.on('reconnect_attempt', (attempt) => {
          if (!mountedRef.current) return;
          setReconnectAttempt(attempt);
        });

        socket.on('reconnect', () => {
          if (!mountedRef.current) return;
          setReconnectAttempt(0);
          fetchMetrics();
        });

        socket.on('event', (event) => {
          if (!mountedRef.current) return;
          applyEvent(event);
        });

        socket.on('delta', (data) => {
          if (!mountedRef.current) return;
          if (data.events && Array.isArray(data.events)) {
            data.events.forEach(applyEvent);
          }
        });

        socket.on('connect_error', (err) => {
          if (!mountedRef.current) return;
          setError(`Erro de conexão: ${err.message}`);
          setLoading(false);
        });

      } catch (e) {
        if (mountedRef.current) {
          setError(e.message || 'Erro ao conectar');
          setLoading(false);
        }
      }
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (socketRef.current) {
        socketRef.current.emit('unsubscribe');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [applyEvent, applySnapshot, fetchMetrics, reconnectAttempt]);

  const rawAgentList = Object.values(state.agents);
  const queueList = Object.values(state.queues);
  const callList = Object.values(state.calls);
  const assignmentList = Object.values(state.assignments);

  // Dedup por extensão (PJSIP + Local da mesma extensão = 1 atendente lógico).
  // O backend já deduz no snapshot inicial; aqui é defesa em profundidade para
  // deltas WebSocket que chegam por interface individual.
  const agentList = React.useMemo(() => {
    const agentMap = new Map();
    const keyFor = (agent) => {
      const iface = agent.interface || '';
      return agent.extension || extractExtension(iface) || iface || agent.memberId || null;
    };

    rawAgentList.forEach(agent => {
      // Defense-in-depth: skip entries that have no usable identity (mirror
      // backend filter at realtime.service.ts; protects against WS deltas).
      if (!isUsefulAgent(agent)) return;
      const key = keyFor(agent);
      if (!key) return;
      const iface = agent.interface || '';

      const existing = agentMap.get(key);
      if (!existing) {
        agentMap.set(key, {
          ...agent,
          extension: agent.extension || extractExtension(iface),
          queues: agent.queue ? [agent.queue] : [],
        });
        return;
      }

      // Canonical = PJSIP (device físico). Se a nova é PJSIP e a anterior não, promove.
      const isPjsipNew = iface.startsWith('PJSIP/');
      const isPjsipOld = (existing.interface || '').startsWith('PJSIP/');
      if (isPjsipNew && !isPjsipOld) {
        existing.interface = iface;
      }
      // Promove memberName apenas se o novo for "limpo" (não começa com PJSIP/...).
      if (agent.memberName) {
        const newClean = !/^(?:PJSIP|Local|SIP|IAX2)\//.test(String(agent.memberName));
        const oldClean = existing.memberName && !/^(?:PJSIP|Local|SIP|IAX2)\//.test(String(existing.memberName));
        if (newClean || !oldClean) existing.memberName = agent.memberName;
      }

      if (agent.queue && !existing.queues.includes(agent.queue)) {
        existing.queues.push(agent.queue);
      }
      existing.inCall = existing.inCall || agent.inCall;
      if (agent.paused) {
        existing.paused = true;
        existing.pausedReason = agent.pausedReason || existing.pausedReason;
      }
      if ((agent.status || 0) > (existing.status || 0)) existing.status = agent.status;
      if ((agent.callsTaken || 0) > (existing.callsTaken || 0)) existing.callsTaken = agent.callsTaken;
      if (agent.displayName && !existing.displayName) existing.displayName = agent.displayName;
    });

    // Segunda passagem: recalcular flags pelo estado ATUAL de TODAS as interfaces
    // de cada extensão (evita "fantasma" de status antigo após mudança).
    const seen = new Set();
    rawAgentList.forEach(agent => {
      if (!isUsefulAgent(agent)) return;
      const key = keyFor(agent);
      if (!key || seen.has(key)) return;
      seen.add(key);
      const existing = agentMap.get(key);
      if (!existing) return;

      const allForKey = rawAgentList.filter(a => isUsefulAgent(a) && keyFor(a) === key);
      existing.inCall = allForKey.some(a => a.inCall);
      const anyPaused = allForKey.some(a => a.paused);
      existing.paused = anyPaused;
      existing.status = Math.max(...allForKey.map(a => a.status || 0), 0);
      if (!anyPaused) existing.pausedReason = '';
    });

    // Annotate effective in-call: AMI inCall flag OR a connected assignment for
    // this extension. Required because Local/<ext>@from-queue QueueMembers (used
    // for simultaneous SIP+WebRTC ringing) never report device-state, so the
    // AMI-only flag is false-negative for those agents.
    const inCallExts = extensionsInActiveAssignments(state.assignments);
    for (const agent of agentMap.values()) {
      const ext = agent.extension ? String(agent.extension) : null;
      agent.effectiveInCall = !!agent.inCall || (!!ext && inCallExts.has(ext));
    }

    return Array.from(agentMap.values());
  }, [rawAgentList, state.assignments]);

  const getQueueAgents = (queueName) => {
    return agentList.filter(a => a.queue === queueName || a.queue?.includes(queueName));
  };

  const getQueueCalls = (queueName) => {
    return callList.filter(c => c.queue === queueName && (c.status === 'queued' || c.status === 'ringing'));
  };

  const getActiveAssignments = (queueName) => {
    return assignmentList.filter(a => a.queue === queueName && a.status === 'connected');
  };

  // Calculate waiting calls from calls list.
  // Defensive: only entries with explicit queued/ringing status, < 4h old.
  // Removed `c.position > 0` heuristic because ghost calls (residual entries from
  // lost call.delete events before B1/B2 fix) also carry a positive position.
  const waitingCalls = callList.filter(c => {
    if (!c.queue) return false;
    if (c.status !== 'queued' && c.status !== 'ringing') return false;
    const elapsed = getElapsedSeconds(c.enteredAt || c.startTime);
    return elapsed >= 0 && elapsed <= 14400;
  });

  // Dedup connected assignments by agent extension. The backend may emit
  // multiple assignment.upsert events for the same extension when SIP+WebRTC
  // ring simultaneously, or leave an orphan record when an `assignment.ended`
  // is lost mid-flight (the `--` row in "Em Atendimento" is a typical symptom).
  // Keep the most recently updated record per extension.
  const connectedAssignments = React.useMemo(() => {
    const byExt = new Map();
    for (const a of assignmentList) {
      if (a.status !== 'connected') continue;
      const ext = a.extension || extractExtension(a.interface || '') || a.assignmentId;
      const stamp = a.updatedAt || a.connectedAt || a.startTime || 0;
      const t = stamp ? new Date(stamp).getTime() : 0;
      const prev = byExt.get(ext);
      if (!prev || t >= prev._ts) {
        byExt.set(ext, { ...a, _ts: t });
      }
    }
    return Array.from(byExt.values());
  }, [assignmentList]);

  // Source preference for "EM ESPERA" card: the per-call list (waitingCalls).
  // It is updated on every call.queued/ringing event in real time, whereas the
  // AMI QueueParams.calls field is a periodic summary that lags. Using the live
  // list also keeps the card aligned with the "Chamadas em Espera" panel below,
  // which is built from the same source.
  const totalWaiting = waitingCalls.length;

  // Calculate active calls. Source of truth = agents with effectiveInCall
  // (which already merges agent.inCall + active assignments). This keeps the
  // "Disponíveis" and "Em Chamada" counters mutually exclusive, eliminating the
  // double-count that used to happen when device state lagged the call state.
  const totalInCall = agentList.filter(a => a.effectiveInCall).length;
  const totalAvailable = agentList.filter(a => !a.paused && !a.effectiveInCall && a.status === 1).length;
  const totalPaused = agentList.filter(a => a.paused).length;

  return (
    <div className="ccv2-container ccv2-container-inlayout">
      {visibility.header && (
        <header className="ccv2-header ccv2-header-compact">
          <div className="ccv2-header-left">
            <h1 className="ccv2-title">Monitoramento em Tempo Real</h1>
            <span className={`ccv2-connection-badge ${connected ? 'connected' : 'disconnected'}`}>
              <span className="ccv2-connection-dot"></span>
              {connected ? 'Conectado' : 'Desconectado'}
            </span>
          </div>
          <div className="ccv2-header-right">
            {reconnectAttempt > 0 && !connected && (
              <span className="ccv2-reconnect-info">
                Reconectando... (tentativa {reconnectAttempt})
              </span>
            )}
            <span className="ccv2-seq">Seq: {state.lastSeq}</span>
          </div>
        </header>
      )}

      {loading && (
        <div className="ccv2-loading">
          <div className="ccv2-spinner"></div>
          <p>Carregando dados...</p>
        </div>
      )}

      {!loading && error && (
        <div className="ccv2-error-banner">
          <span className="ccv2-error-icon">⚠</span>
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && (
        <>
          {visibility.kpiPrimary && (
            <div className="ccv2-kpi-row">
              <div className="ccv2-kpi-card">
                <div className="ccv2-kpi-value">{totalWaiting}</div>
                <div className="ccv2-kpi-label">Em Espera</div>
              </div>
              <div className="ccv2-kpi-card">
                <div className="ccv2-kpi-value ccv2-kpi-green">{totalAvailable}</div>
                <div className="ccv2-kpi-label">Disponíveis</div>
              </div>
              <div className="ccv2-kpi-card">
                <div className="ccv2-kpi-value ccv2-kpi-red">{totalInCall}</div>
                <div className="ccv2-kpi-label">Em Chamada</div>
              </div>
              <div className="ccv2-kpi-card">
                <div className="ccv2-kpi-value ccv2-kpi-purple">{totalPaused}</div>
                <div className="ccv2-kpi-label">Pausados</div>
              </div>
              <div className="ccv2-kpi-card">
                <div className="ccv2-kpi-value">{queueList.length}</div>
                <div className="ccv2-kpi-label">Filas</div>
              </div>
            </div>
          )}

          {visibility.kpiSecondary && (
            <div className="ccv2-kpi-row ccv2-kpi-row-secondary">
              <div className="ccv2-kpi-card ccv2-kpi-compact">
                <div className={`ccv2-kpi-value ${metrics.sla == null ? '' : metrics.sla >= 80 ? 'ccv2-kpi-green' : metrics.sla >= 60 ? 'ccv2-kpi-orange' : 'ccv2-kpi-red'}`}>
                  {metrics.sla == null ? '—' : `${metrics.sla.toFixed(1)}%`}
                </div>
                <div className="ccv2-kpi-label">SLA</div>
                <div className="ccv2-kpi-subtitle">Nível de Serviço</div>
              </div>
              <div className="ccv2-kpi-card ccv2-kpi-compact">
                <div className={`ccv2-kpi-value ${metrics.asa == null ? '' : metrics.asa <= 30 ? 'ccv2-kpi-green' : metrics.asa <= 60 ? 'ccv2-kpi-orange' : 'ccv2-kpi-red'}`}>
                  {metrics.asa == null ? '—' : formatDuration(Math.round(metrics.asa))}
                </div>
                <div className="ccv2-kpi-label">ASA</div>
                <div className="ccv2-kpi-subtitle">Tempo Médio Resposta</div>
              </div>
              <div className="ccv2-kpi-card ccv2-kpi-compact">
                <div className="ccv2-kpi-value">
                  {metrics.aht == null ? '—' : formatDuration(Math.round(metrics.aht))}
                </div>
                <div className="ccv2-kpi-label">AHT</div>
                <div className="ccv2-kpi-subtitle">Tempo Médio Atendimento</div>
              </div>
              <div className="ccv2-kpi-card ccv2-kpi-compact">
                <div className={`ccv2-kpi-value ${metrics.abandonRate == null ? '' : metrics.abandonRate <= 5 ? 'ccv2-kpi-green' : metrics.abandonRate <= 10 ? 'ccv2-kpi-orange' : 'ccv2-kpi-red'}`}>
                  {metrics.abandonRate == null ? '—' : `${metrics.abandonRate.toFixed(1)}%`}
                </div>
                <div className="ccv2-kpi-label">Abandono</div>
                <div className="ccv2-kpi-subtitle">Taxa de Abandono</div>
              </div>
              <div className="ccv2-kpi-card ccv2-kpi-compact">
                <div className={`ccv2-kpi-value ${metrics.occupancy == null ? '' : metrics.occupancy >= 70 ? 'ccv2-kpi-green' : metrics.occupancy >= 50 ? 'ccv2-kpi-orange' : ''}`}>
                  {metrics.occupancy == null ? '—' : `${metrics.occupancy.toFixed(1)}%`}
                </div>
                <div className="ccv2-kpi-label">Ocupação</div>
                <div className="ccv2-kpi-subtitle">Uso dos Agentes</div>
              </div>
            </div>
          )}

          <div className={`ccv2-main-grid ${!visibility.queuesPanel ? 'ccv2-grid-no-queues' : ''} ${!visibility.agentsPanel ? 'ccv2-grid-no-agents' : ''}`}>
            {visibility.queuesPanel && (
              <div className="ccv2-queues-panel">
                <div className="ccv2-panel-header">
                  <h2>Filas</h2>
                </div>
                <div className="ccv2-queue-list">
                  {queueList.length === 0 ? (
                    <p className="ccv2-empty">Nenhuma fila disponível</p>
                  ) : (
                    queueList.map((queue) => {
                      const queueCalls = getQueueCalls(queue.name || queue.queueId);
                      const queueAgents = getQueueAgents(queue.name || queue.queueId);
                      const isSelected = selectedQueue === queue.queueId;
                      // Lifetime AMI counters (queue.completed/abandoned/holdtime/talktime)
                      // are NOT today's stats — use analytics keyed by queue name and
                      // fall back to '—' so we never show stale "since reload" values.
                      const qAnalytics = queueMetrics[queue.name] || queueMetrics[queue.queueId] || null;

                      return (
                        <div
                          key={queue.queueId}
                          className={`ccv2-queue-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => setDrilldownQueue(queue)}
                        >
                          <div className="ccv2-queue-header">
                            <h3 className="ccv2-queue-name" title={queue.name || queue.queueId}>{displayQueue(queue.name || queue.queueId, state.tenantPrefix)}</h3>
                            <span className="ccv2-queue-strategy">{queue.strategy}</span>
                          </div>
                          <div className="ccv2-queue-stats">
                            <div className="ccv2-queue-stat">
                              <span className="ccv2-stat-value ccv2-stat-waiting">{queue.calls || 0}</span>
                              <span className="ccv2-stat-label">Espera</span>
                            </div>
                            <div className="ccv2-queue-stat">
                              <span className="ccv2-stat-value ccv2-stat-completed">{formatQueueCount(qAnalytics, 'calls_answered')}</span>
                              <span className="ccv2-stat-label">Atendidas</span>
                            </div>
                            <div className="ccv2-queue-stat">
                              <span className="ccv2-stat-value ccv2-stat-abandoned">{formatQueueCount(qAnalytics, 'calls_abandoned')}</span>
                              <span className="ccv2-stat-label">Abandonadas</span>
                            </div>
                          </div>
                          <div className="ccv2-queue-metrics">
                            <span>TME: {formatQueueDuration(qAnalytics, 'asa')}</span>
                            <span>TMA: {formatQueueDuration(qAnalytics, 'aht')}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {visibility.callsPanel && (
              <div className="ccv2-calls-panel">
                <div className="ccv2-panel-header">
                  <h2>Chamadas em Espera</h2>
                  <span className="ccv2-panel-count">{totalWaiting}</span>
                </div>
                <div className="ccv2-calls-list">
                  {waitingCalls.length === 0 && totalWaiting === 0 ? (
                    <p className="ccv2-empty">Nenhuma chamada em espera</p>
                  ) : waitingCalls.length === 0 && totalWaiting > 0 ? (
                    <p className="ccv2-empty">{totalWaiting} chamada(s) aguardando (detalhes em breve)</p>
                  ) : (
                    <div className="ccv2-table-wrapper">
                      <table className="ccv2-table">
                        <thead>
                          <tr>
                            <th>Telefone</th>
                            <th>Fila</th>
                            <th>Tempo</th>
                            <th>Posição</th>
                          </tr>
                        </thead>
                        <tbody>
                          {waitingCalls
                            .sort((a, b) => {
                              const timeA = getElapsedSeconds(a.enteredAt || a.startTime) || a.holdTime || 0;
                              const timeB = getElapsedSeconds(b.enteredAt || b.startTime) || b.holdTime || 0;
                              return timeB - timeA;
                            })
                            .map((call) => (
                              <tr key={call.uniqueId || call.callId} className="ccv2-call-row">
                                <td className="ccv2-phone">{call.callerNumber || call.callerIdNum || '--'}</td>
                                <td className="ccv2-queue-cell" title={call.queue}>{displayQueue(call.queue, state.tenantPrefix)}</td>
                                <td className="ccv2-time">
                                  {formatDuration(getElapsedSeconds(call.enteredAt || call.startTime) || call.holdTime || 0)}
                                </td>
                                <td className="ccv2-position">{call.position || '--'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="ccv2-panel-header ccv2-mt">
                  <h2>Em Atendimento</h2>
                  <span className="ccv2-panel-count">{connectedAssignments.length}</span>
                </div>
                <div className="ccv2-calls-list">
                  {connectedAssignments.length === 0 ? (
                    <p className="ccv2-empty">Nenhuma chamada em atendimento</p>
                  ) : (
                    <div className="ccv2-table-wrapper">
                      <table className="ccv2-table">
                        <thead>
                          <tr>
                            <th>Telefone</th>
                            <th>Atendente</th>
                            <th>Fila</th>
                            <th>Tempo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {connectedAssignments
                            .map((assignment) => (
                              <tr key={assignment.assignmentId} className="ccv2-call-row ccv2-call-active">
                                <td className="ccv2-phone">{assignment.callerNumber || '--'}</td>
                                <td>{displayAgentName(assignment)}</td>
                                <td className="ccv2-queue-cell" title={assignment.queue}>{displayQueue(assignment.queue, state.tenantPrefix)}</td>
                                <td className="ccv2-time">{formatDuration(assignment.talkTime || 0)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {visibility.agentsPanel && (
              <div className="ccv2-agents-panel">
                <div className="ccv2-panel-header">
                  <h2>Atendentes</h2>
                  <span className="ccv2-panel-count">{agentList.length}</span>
                </div>
                <div className="ccv2-agents-list">
                  {agentList.length === 0 ? (
                    <p className="ccv2-empty">Nenhum atendente conectado</p>
                  ) : (
                    <div className="ccv2-table-wrapper">
                      <table className="ccv2-table ccv2-agents-table">
                        <thead>
                          <tr>
                            <th>Agente</th>
                            <th>Ramal</th>
                            <th>Fila</th>
                            <th>Pausa</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentList
                            .sort((a, b) => {
                              if (a.effectiveInCall !== b.effectiveInCall) return a.effectiveInCall ? -1 : 1;
                              if (a.paused !== b.paused) return a.paused ? 1 : -1;
                              return (a.memberName || '').localeCompare(b.memberName || '');
                            })
                            .map((agent) => {
                              const ramal = displayRamal(agent);
                              return (
                                <tr key={agent.extension || agent.memberId || agent.interface} className={`ccv2-agent-row ${getAgentStatusClass(agent)}`}>
                                  <td className="ccv2-agent-name">{displayAgentName(agent)}</td>
                                  <td className="ccv2-ramal-cell">{ramal}</td>
                                  <td className="ccv2-agent-queue ccv2-queue-cell" title={agent.queues?.join(', ')}>
                                    {agent.queues?.length > 1
                                      ? `${agent.queues.length} filas`
                                      : displayQueue(agent.queue, state.tenantPrefix)}
                                  </td>
                                  <td>
                                    {agent.paused ? (
                                      <span className="ccv2-pause-badge">{agent.pausedReason || 'Sim'}</span>
                                    ) : (
                                      <span className="ccv2-no-pause">Não</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`ccv2-status-badge ${getAgentStatusClass(agent)}`}>
                                      {getAgentStatusLabel(agent)}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {drilldownQueue && (
        <QueueDrilldown
          queue={drilldownQueue}
          analytics={queueMetrics[drilldownQueue.name] || queueMetrics[drilldownQueue.queueId] || null}
          agents={agentList.filter(a =>
            a.queues?.includes(drilldownQueue.queueKey) ||
            a.queues?.some(q => typeof q === 'object' && q.queueKey === drilldownQueue.queueKey) ||
            a.queue === drilldownQueue.name ||
            a.queue === drilldownQueue.queueKey
          )}
          calls={callList.filter(c => c.queue === drilldownQueue.name || c.queue === drilldownQueue.queueKey || c.queueKey === drilldownQueue.queueKey)}
          assignments={assignmentList.filter(a => a.queue === drilldownQueue.name || a.queue === drilldownQueue.queueKey || a.queueKey === drilldownQueue.queueKey)}
          onClose={() => setDrilldownQueue(null)}
          formatDuration={formatDuration}
          tenantPrefix={state.tenantPrefix}
        />
      )}
    </div>
  );
}

function QueueDrilldown({ queue, analytics, agents, calls, assignments, onClose, formatDuration, tenantPrefix }) {
  const [actionLoading, setActionLoading] = React.useState(null);
  
  const waitingCalls = calls.filter(c => c.status === 'queued' || c.status === 'ringing');
  const activeCalls = assignments.filter(a => a.status === 'connected');
  
  const availableAgents = agents.filter(a => !a.paused && !a.effectiveInCall && a.status === 1);
  const busyAgents = agents.filter(a => a.effectiveInCall);
  const pausedAgents = agents.filter(a => a.paused);

  const handleAgentAction = async (agentKey, action) => {
    try {
      setActionLoading(`${agentKey}-${action}`);
      const apiBase = await getApiBase();
      const token = await getToken();
      const tenantId = await getTenantId();

      let endpoint = '';
      let body = {};

      switch (action) {
        case 'pause':
          endpoint = `/v2/callcenter/agents/${encodeURIComponent(agentKey)}/pause`;
          body = { reason: 'Pausa supervisor' };
          break;
        case 'unpause':
          endpoint = `/v2/callcenter/agents/${encodeURIComponent(agentKey)}/unpause`;
          break;
        case 'logout':
          endpoint = `/v2/callcenter/agents/${encodeURIComponent(agentKey)}/logout`;
          break;
        default:
          return;
      }

      const response = await fetch(`${apiBase}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'Content-Type': 'application/json',
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        const errorMessage = errorData.message || 'Erro ao executar ação';
        console.error(`Erro ao executar ação ${action}:`, errorMessage);
        // Status sync agora garante consistência - erros 409 não devem mais ocorrer
      }
    } catch (e) {
      console.error('Erro na ação do agente:', e);
      alert('Erro ao executar ação. Verifique sua conexão.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="ccv2-drilldown-overlay" onClick={onClose}>
      <div className="ccv2-drilldown-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ccv2-drilldown-header">
          <h2 title={queue.name || queue.queueId}>{displayQueue(queue.name || queue.queueId, tenantPrefix)}</h2>
          <button className="ccv2-drilldown-close" onClick={onClose}>&times;</button>
        </div>
        
        <div className="ccv2-drilldown-content">
          <div className="ccv2-drilldown-stats">
            <div className="ccv2-drilldown-stat">
              <div className="ccv2-drilldown-stat-value">{waitingCalls.length}</div>
              <div className="ccv2-drilldown-stat-label">Em Espera</div>
            </div>
            <div className="ccv2-drilldown-stat">
              <div className="ccv2-drilldown-stat-value">{activeCalls.length}</div>
              <div className="ccv2-drilldown-stat-label">Em Atendimento</div>
            </div>
            <div className="ccv2-drilldown-stat">
              <div className="ccv2-drilldown-stat-value">{availableAgents.length}</div>
              <div className="ccv2-drilldown-stat-label">Disponíveis</div>
            </div>
            <div className="ccv2-drilldown-stat">
              <div className="ccv2-drilldown-stat-value">{busyAgents.length}</div>
              <div className="ccv2-drilldown-stat-label">Ocupados</div>
            </div>
            <div className="ccv2-drilldown-stat">
              <div className="ccv2-drilldown-stat-value">{pausedAgents.length}</div>
              <div className="ccv2-drilldown-stat-label">Pausados</div>
            </div>
            <div className="ccv2-drilldown-stat">
              <div className="ccv2-drilldown-stat-value">{formatQueueCount(analytics, 'calls_answered')}</div>
              <div className="ccv2-drilldown-stat-label">Atendidas Hoje</div>
            </div>
          </div>

          <div className="ccv2-drilldown-section">
            <h3>Agentes ({agents.length})</h3>
            {agents.length === 0 ? (
              <p className="ccv2-empty">Nenhum agente nesta fila</p>
            ) : (
              <table className="ccv2-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    <th>Ramal</th>
                    <th>Status</th>
                    <th>Pausa</th>
                    <th>Chamadas</th>
                    <th>Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => {
                    const ramal = displayRamal(agent);
                    const agentKey = agent.interface || agent.memberId;
                    let statusLabel = 'Offline';
                    let statusClass = 'offline';
                    
                    if (agent.effectiveInCall) {
                      statusLabel = 'Em chamada';
                      statusClass = 'busy';
                    } else if (agent.paused) {
                      statusLabel = 'Pausado';
                      statusClass = 'paused';
                    } else if (agent.status === 1) {
                      statusLabel = 'Disponível';
                      statusClass = 'available';
                    }

                    const isLoading = actionLoading?.startsWith(agentKey);

                    return (
                      <tr key={agent.extension || agent.memberId || agent.interface} className={`ccv2-agent-row ${statusClass}`}>
                        <td>{displayAgentName(agent)}</td>
                        <td className="ccv2-ramal-cell">{ramal}</td>
                        <td>
                          <span className={`ccv2-status-badge ${statusClass}`}>{statusLabel}</span>
                        </td>
                        <td>{agent.pausedReason || '-'}</td>
                        <td>{agent.callsTaken || 0}</td>
                        <td className="ccv2-actions-cell">
                          {agent.paused ? (
                            <button
                              className="ccv2-action-btn ccv2-action-btn-unpause"
                              onClick={() => handleAgentAction(agentKey, 'unpause')}
                              disabled={isLoading}
                            >
                              {isLoading ? '...' : 'Despausar'}
                            </button>
                          ) : (agent.status === 1 || agent.status === 4 || agent.status === 5) && !agent.effectiveInCall && !agent.paused ? (
                            <button
                              className="ccv2-action-btn ccv2-action-btn-pause"
                              onClick={() => handleAgentAction(agentKey, 'pause')}
                              disabled={isLoading}
                            >
                              {isLoading ? '...' : 'Pausar'}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {waitingCalls.length > 0 && (
            <div className="ccv2-drilldown-section">
              <h3>Chamadas em Espera ({waitingCalls.length})</h3>
              <table className="ccv2-table">
                <thead>
                  <tr>
                    <th>Telefone</th>
                    <th>Tempo</th>
                    <th>Posicao</th>
                  </tr>
                </thead>
                <tbody>
                  {waitingCalls.map((call) => (
                    <tr key={call.uniqueId || call.callId}>
                      <td>{call.callerNumber || '--'}</td>
                      <td>{formatDuration(call.holdTime || 0)}</td>
                      <td>{call.position || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeCalls.length > 0 && (
            <div className="ccv2-drilldown-section">
              <h3>Em Atendimento ({activeCalls.length})</h3>
              <table className="ccv2-table">
                <thead>
                  <tr>
                    <th>Telefone</th>
                    <th>Agente</th>
                    <th>Duracao</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCalls.map((assignment) => (
                    <tr key={assignment.assignmentId}>
                      <td>{assignment.callerNumber || '--'}</td>
                      <td>{displayAgentName(assignment)}</td>
                      <td>{formatDuration(assignment.talkTime || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CallcenterV2;
